import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Snackbar,
  SvgIcon,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { apiRequest } from "../../utils/apiClient";

/* =====================================================
 * ENUMS
 * ===================================================== */
enum RoleName {
  CLIENT = "CLIENT",
  SUPER_ADMIN = "SUPERADMIN",
}

enum StoreName {
  WAREHOUSE_CLIENT = "Warehouse Client",
}

type SnackbarSeverity = "success" | "error" | "warning" | "info";

type FormField =
  | "first_name"
  | "last_name"
  | "email"
  | "password"
  | "confirmPassword"
  | "role_id"
  | "store_id"
  | "discount_percent";

type TextInputField =
  | "first_name"
  | "last_name"
  | "email"
  | "password"
  | "confirmPassword";

/* =====================================================
 * TYPES
 * ===================================================== */
interface RegistrationFormData {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  confirmPassword: string;
  store_id: string;
  store_name: string;
  role_id: string;
  role_name: string;
  discount_percent: number;
}

interface RegisterPayload {
  username: string;
  email: string;
  password: string;
  role_id: string;
  first_name: string;
  last_name: string;
  store_id?: string;
  discount_percent: number;
}

interface RegisterResponse {
  message: string;
}

interface StoreOption {
  store_id: string;
  name: string;
}

interface RoleOption {
  role_id: string;
  name: string;
}

type FormErrors = Partial<Record<FormField, string>>;

/* =====================================================
 * INITIAL STATE
 * ===================================================== */
const INITIAL_FORM_DATA: RegistrationFormData = {
  first_name: "",
  last_name: "",
  email: "",
  password: "",
  confirmPassword: "",
  store_id: "",
  store_name: "",
  role_id: "",
  role_name: "",
  discount_percent: 0,
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/* =====================================================
 * ICONS
 * ===================================================== */
const AdminShieldIcon = () => (
  <SvgIcon sx={{ fontSize: { xs: 28, md: 34 } }}>
    <path d="M12 2 3 6v6c0 5.5 3.8 9.7 9 10 5.2-.3 9-4.5 9-10V6l-9-4Zm0 4.2a3 3 0 1 1 0 6 3 3 0 0 1 0-6Zm0 12.2c-2.2 0-4.1-.9-5.4-2.2.1-2 3.6-3.1 5.4-3.1 1.8 0 5.3 1.1 5.4 3.1-1.3 1.3-3.2 2.2-5.4 2.2Z" />
  </SvgIcon>
);

const VisibilityIcon = () => (
  <SvgIcon fontSize="small">
    <path d="M12 5C7.5 5 3.7 7.7 2 12c1.7 4.3 5.5 7 10 7s8.3-2.7 10-7c-1.7-4.3-5.5-7-10-7Zm0 11a4 4 0 1 1 0-8 4 4 0 0 1 0 8Zm0-2.2a1.8 1.8 0 1 1 0-3.6 1.8 1.8 0 0 1 0 3.6Z" />
  </SvgIcon>
);

const VisibilityOffIcon = () => (
  <SvgIcon fontSize="small">
    <path d="M12 5c4.5 0 8.3 2.7 10 7-.7 1.8-1.8 3.3-3.1 4.5l-1.5-1.5A9.3 9.3 0 0 0 19.7 12C18.2 8.9 15.4 7 12 7c-.8 0-1.5.1-2.2.3L8.1 5.6C9.3 5.2 10.6 5 12 5ZM3.3 3 21 20.7 19.7 22l-3.2-3.2c-1.4.7-2.9 1.2-4.5 1.2-4.5 0-8.3-2.7-10-7 .8-2.1 2.2-3.9 3.9-5.1L2 4.3 3.3 3Zm4.1 6.4A6.6 6.6 0 0 0 4.3 13c1.5 3.1 4.3 5 7.7 5 1 0 1.9-.2 2.8-.5l-1.6-1.6c-.4.1-.8.1-1.2.1a4 4 0 0 1-4-4c0-.4 0-.8.1-1.2L7.4 9.4Zm4.5-1.4h.1a4 4 0 0 1 4 4v.1L11.9 8Z" />
  </SvgIcon>
);

/* =====================================================
 * HELPERS
 * ===================================================== */
const normalizeRoleName = (role?: string | null): string =>
  role?.replace(/[\s_-]/g, "").toUpperCase() ?? "";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const getErrorMessage = (error: unknown): string => {
  if (!isRecord(error)) {
    return "User registration failed.";
  }

  const response = error.response;

  if (isRecord(response)) {
    const data = response.data;

    if (
      isRecord(data) &&
      typeof data.message === "string" &&
      data.message.trim()
    ) {
      return data.message;
    }
  }

  if (typeof error.message === "string" && error.message.trim()) {
    return error.message;
  }

  return "User registration failed.";
};

const registerUser = async (
  payload: RegisterPayload,
): Promise<RegisterResponse> => {
  const response = await axios.post("/api/auth/register", payload);
  return response.data as RegisterResponse;
};

/* =====================================================
 * COMPONENT
 * ===================================================== */
const RegistrationPage = () => {
  const authUser = useSelector((state: RootState) => state.auth.user);

  const [formData, setFormData] =
    useState<RegistrationFormData>(INITIAL_FORM_DATA);

  const [stores, setStores] = useState<StoreOption[]>([]);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const [isLoadingOptions, setIsLoadingOptions] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] =
    useState<SnackbarSeverity>("success");

  const isClientRole =
    normalizeRoleName(formData.role_name) === RoleName.CLIENT;

  const isCurrentUserSuperAdmin =
    normalizeRoleName(authUser?.role) === RoleName.SUPER_ADMIN;

  const hasValidationErrors = Object.keys(formErrors).length > 0;

  /* =====================================================
   * SNACKBAR
   * ===================================================== */
  const showSnackbar = useCallback(
    (message: string, severity: SnackbarSeverity) => {
      setSnackbarMessage(message);
      setSnackbarSeverity(severity);
      setOpenSnackbar(true);
    },
    [],
  );

  /* =====================================================
   * LOAD STORES + ROLES
   * ===================================================== */
  useEffect(() => {
    const loadRegistrationOptions = async () => {
      setIsLoadingOptions(true);

      try {
        const [storesResponse, rolesResponse] = await Promise.all([
          apiRequest("/api/stores/list", "POST"),
          apiRequest("/api/roles/list", "POST"),
        ]);

        const storesData = storesResponse.data as unknown;
        const rolesData = rolesResponse.data as unknown;

        const storeRows: StoreOption[] = Array.isArray(storesData)
          ? (storesData as StoreOption[])
          : [];

        const roleRows: RoleOption[] =
          isRecord(rolesData) && Array.isArray(rolesData.rows)
            ? (rolesData.rows as RoleOption[])
            : [];

        const availableRoles = isCurrentUserSuperAdmin
          ? roleRows
          : roleRows.filter(
              (role) => normalizeRoleName(role.name) !== RoleName.SUPER_ADMIN,
            );

        setStores(storeRows);
        setRoles(availableRoles);
      } catch (error: unknown) {
        console.error("Error loading registration options:", error);

        setStores([]);
        setRoles([]);

        showSnackbar(
          "Unable to load stores and roles. Please refresh and try again.",
          "error",
        );
      } finally {
        setIsLoadingOptions(false);
      }
    };

    void loadRegistrationOptions();
  }, [isCurrentUserSuperAdmin, showSnackbar]);

  /* =====================================================
   * FORM HELPERS
   * ===================================================== */
  const clearFieldError = (field: FormField) => {
    setFormErrors((previous) => {
      if (!previous[field]) {
        return previous;
      }

      const updated = { ...previous };
      delete updated[field];

      return updated;
    });
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const field = event.target.name as TextInputField;
    const { value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [field]: value,
    }));

    clearFieldError(field);

    if (field === "password") {
      clearFieldError("confirmPassword");
    }
  };

  /* =====================================================
   * ROLE CHANGE
   * ===================================================== */
  const handleRoleChange = (event: SelectChangeEvent<string>) => {
    const selectedRole = roles.find(
      (role) => role.role_id === event.target.value,
    );

    if (!selectedRole) {
      return;
    }

    const selectedRoleIsClient =
      normalizeRoleName(selectedRole.name) === RoleName.CLIENT;

    const previousRoleWasClient =
      normalizeRoleName(formData.role_name) === RoleName.CLIENT;

    if (selectedRoleIsClient) {
      const warehouseClientStore = stores.find(
        (store) => store.name === StoreName.WAREHOUSE_CLIENT,
      );

      setFormData((previous) => ({
        ...previous,
        role_id: selectedRole.role_id,
        role_name: selectedRole.name,
        store_id: warehouseClientStore?.store_id ?? "",
        store_name: warehouseClientStore?.name ?? "",
      }));

      if (!warehouseClientStore) {
        showSnackbar(
          `"${StoreName.WAREHOUSE_CLIENT}" store could not be found.`,
          "error",
        );
      }
    } else {
      setFormData((previous) => ({
        ...previous,
        role_id: selectedRole.role_id,
        role_name: selectedRole.name,
        ...(previousRoleWasClient
          ? {
              store_id: "",
              store_name: "",
              discount_percent: 0,
            }
          : {}),
      }));
    }

    clearFieldError("role_id");
    clearFieldError("store_id");
    clearFieldError("discount_percent");
  };

  /* =====================================================
   * STORE CHANGE
   * ===================================================== */
  const handleStoreChange = (event: SelectChangeEvent<string>) => {
    const selectedStore = stores.find(
      (store) => store.store_id === event.target.value,
    );

    if (!selectedStore) {
      return;
    }

    setFormData((previous) => ({
      ...previous,
      store_id: selectedStore.store_id,
      store_name: selectedStore.name,
    }));

    clearFieldError("store_id");
  };

  /* =====================================================
   * DISCOUNT CHANGE
   * ===================================================== */
  const handleDiscountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setFormData((previous) => ({
      ...previous,
      discount_percent: value === "" ? 0 : Number(value),
    }));

    clearFieldError("discount_percent");
  };

  /* =====================================================
   * VALIDATION
   * ===================================================== */
  const validateForm = (): FormErrors => {
    const errors: FormErrors = {};

    if (!formData.first_name.trim()) {
      errors.first_name = "First name is required.";
    }

    if (!formData.last_name.trim()) {
      errors.last_name = "Last name is required.";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required.";
    } else if (!EMAIL_PATTERN.test(formData.email.trim())) {
      errors.email = "Please enter a valid email address.";
    }

    if (!formData.role_id) {
      errors.role_id = "Role is required.";
    }

    if (!formData.password) {
      errors.password = "Password is required.";
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = "Please confirm the password.";
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match.";
    }

    if (isClientRole && !formData.store_id) {
      errors.store_id = `"${StoreName.WAREHOUSE_CLIENT}" is required for Client accounts.`;
    }

    if (
      isClientRole &&
      (!Number.isFinite(formData.discount_percent) ||
        formData.discount_percent < 0 ||
        formData.discount_percent > 100)
    ) {
      errors.discount_percent =
        "Discount percentage must be between 0 and 100.";
    }

    return errors;
  };

  /* =====================================================
   * SUBMIT
   * ===================================================== */
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      return;
    }

    setFormErrors({});
    setIsSubmitting(true);

    const email = formData.email.trim();

    const registerPayload: RegisterPayload = {
      username: email,
      email,
      password: formData.password,
      role_id: formData.role_id,
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
      discount_percent: isClientRole ? formData.discount_percent : 0,
      ...(formData.store_id
        ? {
            store_id: formData.store_id,
          }
        : {}),
    };

    try {
      const response = await registerUser(registerPayload);

      setFormData({ ...INITIAL_FORM_DATA });
      setFormErrors({});
      setShowPassword(false);
      setShowConfirmPassword(false);

      showSnackbar(
        response.message || "User registered successfully.",
        "success",
      );
    } catch (error: unknown) {
      console.error("Registration error:", error);
      showSnackbar(getErrorMessage(error), "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  /* =====================================================
   * COMMON FIELD STYLES
   * ===================================================== */
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 1.5,
    },
  };

  /* =====================================================
   * UI
   * ===================================================== */
  return (
    <Box
      sx={{
        width: "100%",
        minHeight: {
          xs: "auto",
          md: "calc(100dvh - 100px)",
        },
        display: "flex",
        alignItems: {
          xs: "flex-start",
          md: "center",
        },
        justifyContent: "center",
        px: {
          xs: 1.5,
          sm: 2,
          md: 3,
        },
        py: {
          xs: 1.5,
          sm: 2,
          md: 2,
        },
        boxSizing: "border-box",
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: "100%",
          maxWidth: 1100,
          mx: "auto",
          overflow: "hidden",
          borderRadius: {
            xs: 2,
            md: 3,
          },
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Grid
          container
          sx={{
            minHeight: {
              xs: "auto",
              md: 560,
            },
          }}
        >
          {/* =============================================
           * LEFT PANEL
           * ============================================= */}
          <Grid
            size={{ xs: 12, md: 4 }}
            sx={{
              display: {
                xs: "none",
                md: "flex",
              },
              flexDirection: "column",
              justifyContent: "space-between",
              p: {
                md: 3.5,
                lg: 4,
              },
              backgroundColor: "#FAFCFA",
              borderRight: "1px solid #E5E7EB",
            }}
          >
            <Box>
              {/* ICON */}
              <Box
                sx={{
                  width: 54,
                  height: 54,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 2,
                  mb: 3,
                  color: "#0B5D1E",
                  backgroundColor: "#EAF5EC",
                  border: "1px solid #D5E8D9",
                }}
              >
                <AdminShieldIcon />
              </Box>

              {/* TITLE */}
              <Typography
                variant="h4"
                sx={{
                  fontSize: {
                    md: "1.75rem",
                    lg: "2rem",
                  },
                  fontWeight: 700,
                  lineHeight: 1.2,
                  color: "#1F2937",
                  mb: 1.25,
                }}
              >
                User Registration
              </Typography>

              {/* DESCRIPTION */}
              <Typography
                variant="body2"
                sx={{
                  color: "#6B7280",
                  lineHeight: 1.7,
                  maxWidth: 290,
                  mb: 4,
                }}
              >
                Create new user accounts and assign the appropriate role and
                location.
              </Typography>

              {/* DIVIDER */}
              <Box
                sx={{
                  width: 42,
                  height: 3,
                  borderRadius: 2,
                  backgroundColor: "#0B5D1E",
                  mb: 3,
                }}
              />

              {/* FEATURES */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.25,
                }}
              >
                {[
                  {
                    title: "Access Control",
                    description: "Assign the appropriate user role",
                  },
                  {
                    title: "Store Assignment",
                    description: "Connect the user to the correct location",
                  },
                  {
                    title: "Secure Account",
                    description: "Create protected login credentials",
                  },
                ].map((item) => (
                  <Box
                    key={item.title}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "50%",
                        color: "#0B5D1E",
                        backgroundColor: "#EAF5EC",
                        fontSize: 14,
                        fontWeight: 700,
                        mt: 0.25,
                      }}
                    >
                      ✓
                    </Box>

                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#1F2937",
                          fontWeight: 600,
                          lineHeight: 1.4,
                        }}
                      >
                        {item.title}
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          color: "#9CA3AF",
                          mt: 0.25,
                          lineHeight: 1.4,
                        }}
                      >
                        {item.description}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* BOTTOM */}
            <Box
              sx={{
                pt: 2,
                borderTop: "1px solid #E5E7EB",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: "#9CA3AF",
                  mb: 0.25,
                }}
              >
                Administration Portal
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  color: "#0B5D1E",
                  fontWeight: 600,
                }}
              >
                User & access management
              </Typography>
            </Box>
          </Grid>

          {/* =============================================
           * FORM PANEL
           * ============================================= */}
          <Grid
            size={{ xs: 12, md: 8 }}
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              p: {
                xs: 2,
                sm: 3,
                md: 3.5,
                lg: 4,
              },
              backgroundColor: "#ffffff",
            }}
          >
            {/* MOBILE HEADER */}
            <Box
              sx={{
                display: {
                  xs: "flex",
                  md: "none",
                },
                alignItems: "center",
                gap: 1.5,
                mb: 2.5,
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  borderRadius: 1.5,
                  color: "primary.main",
                  backgroundColor: "#E0F2FE",
                }}
              >
                <AdminShieldIcon />
              </Box>

              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  User Registration
                </Typography>

                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Create a new user account
                </Typography>
              </Box>
            </Box>

            {/* DESKTOP HEADER */}
            <Box
              sx={{
                display: {
                  xs: "none",
                  md: "block",
                },
                mb: 3,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                  mb: 0.5,
                }}
              >
                Create User
              </Typography>

              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Enter the user's information and assign the appropriate access
                level.
              </Typography>
            </Box>

            <Box
              component="form"
              onSubmit={handleSubmit}
              noValidate
              autoComplete="off"
            >
              <Grid
                container
                spacing={{
                  xs: 1.75,
                  sm: 2,
                  md: 2,
                }}
              >
                {/* FIRST NAME */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="First Name"
                    name="first_name"
                    type="text"
                    size="small"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    error={Boolean(formErrors.first_name)}
                    helperText={formErrors.first_name}
                    autoComplete="off"
                    fullWidth
                    required
                    disabled={isSubmitting}
                    sx={fieldSx}
                  />
                </Grid>

                {/* LAST NAME */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Last Name"
                    name="last_name"
                    type="text"
                    size="small"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    error={Boolean(formErrors.last_name)}
                    helperText={formErrors.last_name}
                    autoComplete="off"
                    fullWidth
                    required
                    disabled={isSubmitting}
                    sx={fieldSx}
                  />
                </Grid>

                {/* EMAIL */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Email"
                    name="email"
                    type="email"
                    size="small"
                    value={formData.email}
                    onChange={handleInputChange}
                    error={Boolean(formErrors.email)}
                    helperText={formErrors.email ?? "Used as the username."}
                    autoComplete="off"
                    fullWidth
                    required
                    disabled={isSubmitting}
                    sx={fieldSx}
                  />
                </Grid>

                {/* ROLE */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl
                    fullWidth
                    required
                    size="small"
                    error={Boolean(formErrors.role_id)}
                    disabled={isLoadingOptions || isSubmitting}
                    sx={fieldSx}
                  >
                    <InputLabel id="registration-role-label">Role</InputLabel>

                    <Select
                      labelId="registration-role-label"
                      id="registration-role"
                      name="role_id"
                      value={formData.role_id}
                      label="Role"
                      onChange={handleRoleChange}
                    >
                      {roles.length > 0 ? (
                        roles.map((role) => (
                          <MenuItem key={role.role_id} value={role.role_id}>
                            {role.name}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value="" disabled>
                          No roles available
                        </MenuItem>
                      )}
                    </Select>

                    {formErrors.role_id && (
                      <FormHelperText>{formErrors.role_id}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                {/* STORE */}
                <Grid
                  size={{
                    xs: 12,
                    sm: isClientRole ? 6 : 12,
                  }}
                >
                  <FormControl
                    fullWidth
                    size="small"
                    error={Boolean(formErrors.store_id)}
                    disabled={isLoadingOptions || isSubmitting || isClientRole}
                    sx={fieldSx}
                  >
                    <InputLabel id="registration-store-label">Store</InputLabel>

                    <Select
                      labelId="registration-store-label"
                      id="registration-store"
                      name="store_id"
                      value={formData.store_id}
                      label="Store"
                      onChange={handleStoreChange}
                    >
                      {stores.length > 0 ? (
                        stores.map((store) => (
                          <MenuItem key={store.store_id} value={store.store_id}>
                            {store.name}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem value="" disabled>
                          No stores available
                        </MenuItem>
                      )}
                    </Select>

                    {formErrors.store_id ? (
                      <FormHelperText>{formErrors.store_id}</FormHelperText>
                    ) : isClientRole ? (
                      <FormHelperText>
                        Automatically assigned to {StoreName.WAREHOUSE_CLIENT}.
                      </FormHelperText>
                    ) : null}
                  </FormControl>
                </Grid>

                {/* CLIENT DISCOUNT */}
                {isClientRole && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Discount Percentage"
                      name="discount_percent"
                      type="number"
                      size="small"
                      value={formData.discount_percent}
                      onChange={handleDiscountChange}
                      error={Boolean(formErrors.discount_percent)}
                      helperText={formErrors.discount_percent ?? "0% to 100%."}
                      fullWidth
                      required
                      disabled={isSubmitting}
                      slotProps={{
                        htmlInput: {
                          min: 0,
                          max: 100,
                          step: 0.01,
                        },
                      }}
                      sx={fieldSx}
                    />
                  </Grid>
                )}

                {/* PASSWORD */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    size="small"
                    value={formData.password}
                    onChange={handleInputChange}
                    error={Boolean(formErrors.password)}
                    helperText={formErrors.password}
                    autoComplete="new-password"
                    fullWidth
                    required
                    disabled={isSubmitting}
                    sx={fieldSx}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              edge="end"
                              size="small"
                              aria-label={
                                showPassword ? "Hide password" : "Show password"
                              }
                              onClick={() =>
                                setShowPassword((previous) => !previous)
                              }
                              onMouseDown={(event) => event.preventDefault()}
                            >
                              {showPassword ? (
                                <VisibilityOffIcon />
                              ) : (
                                <VisibilityIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Grid>

                {/* CONFIRM PASSWORD */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Confirm Password"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    size="small"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    error={Boolean(formErrors.confirmPassword)}
                    helperText={formErrors.confirmPassword}
                    autoComplete="new-password"
                    fullWidth
                    required
                    disabled={isSubmitting}
                    sx={fieldSx}
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              edge="end"
                              size="small"
                              aria-label={
                                showConfirmPassword
                                  ? "Hide confirm password"
                                  : "Show confirm password"
                              }
                              onClick={() =>
                                setShowConfirmPassword((previous) => !previous)
                              }
                              onMouseDown={(event) => event.preventDefault()}
                            >
                              {showConfirmPassword ? (
                                <VisibilityOffIcon />
                              ) : (
                                <VisibilityIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Grid>

                {/* GENERAL VALIDATION */}
                {hasValidationErrors && (
                  <Grid size={12}>
                    <Alert
                      severity="error"
                      sx={{
                        py: 0.25,
                        alignItems: "center",
                      }}
                    >
                      Please review the highlighted fields.
                    </Alert>
                  </Grid>
                )}

                {/* SUBMIT */}
                <Grid size={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={
                      isSubmitting || isLoadingOptions || roles.length === 0
                    }
                    sx={{
                      height: 44,
                      mt: 0.5,
                      borderRadius: 1.5,
                      textTransform: "none",
                      fontSize: 15,
                      fontWeight: 600,
                    }}
                  >
                    {isSubmitting ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <CircularProgress size={18} color="inherit" />
                        Creating User...
                      </Box>
                    ) : (
                      "Create User"
                    )}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          severity={snackbarSeverity}
          onClose={() => setOpenSnackbar(false)}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RegistrationPage;
